import { ChangeDetectionStrategy, Component } from '@angular/core';
import { Router } from '@angular/router';
import { APP_PRIVATE_PATHS } from '@epicuro-next/navigation/constants';
import { selectActiveAccountBrandOrg } from '@epicuro-next/state/accounts-state';
import { selectBestPerformingDashboard } from '@epicuro-next/state/analytics-entities';
import { selectBestListMinimumMonthsLimitCalendarMinDate } from '@epicuro-next/state/product-list-state';
import { Store } from '@ngrx/store';
import { map, take, withLatestFrom } from 'rxjs/operators';

import { dateFilterOptions } from '../../data';
import { BestPerformingListQuery } from '../../models';
import { BestPerformingListsStore } from '../../store';

@Component({
  selector: 'epc-best-performing-lists',
  templateUrl: './best-performing-lists.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [BestPerformingListsStore],
  standalone: false,
})
export class BestPerformingListsComponent {
  public bestPerformingLists$ = this.componentStore.bestPerformingLists$;
  public loading$ = this.componentStore.loading$;
  public currentUserExternalId$ = this.componentStore.currentUserExternalId$;
  public brandOrgFilter$ = this.componentStore.brandOrgFilter$;
  public selectedBrandOrgIds$ = this.componentStore.selectedBrandOrgIds$;
  public brandOrgsFilterEnabled$ = this.componentStore.brandOrgsFilterEnabled$;
  public hasError$ = this.componentStore.hasError$;

  public exchange$ = this.componentStore.analyticsListExchange$;

  private bestPerformingListDashboardSettings$ = this.store
    .select(selectBestPerformingDashboard)
    .pipe(
      map(
        (bestPerformingDashboardSettings) =>
          bestPerformingDashboardSettings.dashboards?.lists,
      ),
    );

  public showHotIconBadges$ = this.bestPerformingListDashboardSettings$.pipe(
    map((settings) => settings?.showHotIconBadges),
  );

  public preselectedDateRangeFilterValue$ =
    this.bestPerformingListDashboardSettings$.pipe(
      map((bestPerformingListDashboardSettings) => {
        const valueFromConfig =
          bestPerformingListDashboardSettings?.preselectedDateRangeFilterValue?.toLowerCase();
        const dateFilterOptionFound = dateFilterOptions.find(
          (dfo) => dfo.id.toLowerCase() === valueFromConfig,
        );
        return dateFilterOptionFound?.id ?? dateFilterOptions[0].id;
      }),
    );

  private preselectBrandOrgFromAccountByDefault$ =
    this.bestPerformingListDashboardSettings$.pipe(
      map(
        (bestPerformingListDashboardSettings) =>
          bestPerformingListDashboardSettings?.brandOrgFilter?.enabled &&
          bestPerformingListDashboardSettings?.brandOrgFilter
            ?.selectFromAccountByDefault,
      ),
    );

  public preselectedBrandOrg$ = this.store
    .select(selectActiveAccountBrandOrg)
    .pipe(
      withLatestFrom(this.preselectBrandOrgFromAccountByDefault$),
      map(([activeAccountBrandOrg, preselectBrandOrgFromAccountByDefault]) => {
        if (!preselectBrandOrgFromAccountByDefault) {
          return;
        }

        return activeAccountBrandOrg;
      }),
    );

  public bestListMinDate$ = this.store.select(
    selectBestListMinimumMonthsLimitCalendarMinDate,
  );

  constructor(
    private readonly componentStore: BestPerformingListsStore,
    private readonly store: Store,
    private readonly router: Router,
  ) {
    this.preselectedBrandOrg$.pipe(take(1)).subscribe((preselectedBrandOrg) => {
      if (preselectedBrandOrg) {
        this.setStoreParams({
          brandOrgIds: [preselectedBrandOrg],
        });
      }
    });
  }

  public setStoreParams(payload: Partial<BestPerformingListQuery>) {
    this.componentStore.setParams(payload);
  }

  public onListSelect(listId: string) {
    this.componentStore.setSource(listId);
    this.router.navigate([
      APP_PRIVATE_PATHS.browse.path,
      APP_PRIVATE_PATHS.productList.path,
      listId,
    ]);
  }
}
